/**
 * @fileoverview Hook de UI Layer para definir la estructura y contenidos de los paneles del acordeón de detalles del cliente.
 * @module useCustomerDetailsAccordionPanelsUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.2.0
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ConversationTopicsMC from '../../../modules/inbox/components/ConversationTopicsMC';
import CustomerAddressesMC from '../../../modules/inbox/components/CustomerAddressesMC';
import CustomerNotesMC from '../../../modules/inbox/components/CustomerNotesMC';
import CustomerEventsMC from '../../../modules/inbox/components/CustomerEventsMC';
import CustomerCallsMC from '../../../modules/inbox/components/CustomerCallsMC';
import CustomerDocumentsModuleMC from '../../../modules/Customers/Documents/CustomerDocumentsModuleMC';
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';
import { useWhatsappCallsConnected } from '../../calls/useCallProvider';
import LeadScoreDetailMC from '../../../modules/inbox/components/LeadScoreDetailMC';
import { isInboxMsChatPlatform } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Devuelve la estructura de paneles del acordeón para CustomerDetailsAccordionMC, generando los contenidos internamente.
 *
 * @param {Object} params
 * @param {Object} params.conversation - Objeto de conversación
 * @param {Object} params.customer - Objeto de cliente
 * @returns {Array<Object>} Arreglo de paneles para el acordeón
 *
 * @example
 * const accordionPanels = useCustomerDetailsAccordionPanelsUI({ conversation, customer });
 */
function useCustomerDetailsAccordionPanelsUI({ conversation, customer }) {
  const { t } = useTranslation('InboxMC');
  const isWhatsappCallsEnabled = useWhatsappCallsConnected();
  const { leadScore, leadScoreLoading } = useSelector((state) => state.inboxRightReducer);
  const retailerInfo = useSelector((state) => state.retailerUsersReducer.retailer_info);

  // Contenidos de cada panel (memoizados)
  const conversationTopicsContent = useMemo(() => (
    conversation?.id ? (
      <ConversationTopicsMC
        key={`conversation-topics-${conversation.id}`}
        conversation={conversation}
      />
    ) : null
  ), [conversation]);

  const customerAddressesContent = useMemo(() => (
    customer?.id ? (
      <CustomerAddressesMC
        key={`customer-addresses-${customer.id}`}
        customer={customer}
      />
    ) : null
  ), [customer]);

  const CustomerNotesContent = useMemo(() => (
    conversation?.id ? (
      <CustomerNotesMC
        platform={conversation.platform}
      />
    ) : null
  ), [conversation]);

  const CustomerEventsContent = useMemo(() => {
    if (!conversation?.id) return null;
    const isMs = isInboxMsChat(conversation.platform);
    const chatId = isMs ? conversation.id : undefined;

    return <CustomerEventsMC customer={customer} chatId={chatId} />;
  }, [conversation, customer]);

  const customerDocumentsContent = useMemo(() => (
    customer?.id ? (
      <CustomerDocumentsModuleMC
        customerId={customer.id}
        source="inbox"
        compact
      />
    ) : null
  ), [customer]);

  const customerCallsContent = useMemo(() => (
    customer?.id ? (
      <CustomerCallsMC
        key={`customer-calls-${customer.id}`}
        customer={customer}
      />
    ) : null
  ), [customer]);

  const leadScoreContent = useMemo(() => (
    <LeadScoreDetailMC
      leadScore={leadScore}
      loading={leadScoreLoading}
      customerId={customer?.id}
      platform={conversation?.platform}
    />
  ), [leadScore, leadScoreLoading, customer?.id, conversation?.platform]);

  // Estructura de paneles
  return useMemo(() => {
    const panels = [];

    if (retailerInfo?.ai_copilots_enabled) {
      panels.push({
        key: 'lead_score',
        title: t('copilot_ventas.title'),
        content: leadScoreContent
      });
    }

    panels.push({
      key: 'documents',
      title: t('customerDetails.accordion.options.documents'),
      content: customerDocumentsContent
    });

    panels.push({
      key: 'attributes',
      title: t('customerDetails.accordion.options.conversation_attributes'),
      content: conversationTopicsContent
    });

    panels.push(
      {
        key: 'addresses',
        title: t('customerDetails.accordion.options.addresses'),
        content: customerAddressesContent
      },
      {
        key: 'notes',
        title: t('customerDetails.accordion.options.notes'),
        content: CustomerNotesContent
      }
    );

    if (isWhatsappCallsEnabled) {
      panels.push({
        key: 'calls',
        title: t('customerDetails.accordion.options.calls'),
        content: customerCallsContent
      });
    }

    panels.push({
      key: 'activities',
      title: t('customerDetails.accordion.options.activities'),
      content: CustomerEventsContent
    });

    return panels;
  }, [
    t,
    isWhatsappCallsEnabled,
    retailerInfo?.ai_copilots_enabled,
    leadScoreContent,
    conversationTopicsContent,
    customerAddressesContent,
    CustomerNotesContent,
    CustomerEventsContent,
    customerDocumentsContent,
    customerCallsContent
  ]);
}

export default useCustomerDetailsAccordionPanelsUI;
